import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { SHAPE_MAP } from "../../lib/sewingShapes";
import { buildTeddy, JOINTS } from "../../lib/sewingComposites";
import { labHref } from "../../lib/sewingLinks";
import FoldViewer from "./FoldViewer";

/* ============================================================
   AssemblyLab — the teddy, exploded into its primitive parts,
   with the three joint types color-coded where parts meet.
   Attaching is the third skill (after folding and stretching),
   which is why this lives on the Make page.
   ============================================================ */

const AssemblyLab = () => {
  const teddy = useMemo(() => buildTeddy(), []);
  // Chips: one per distinct part name, carrying that part's lab params.
  const chips = [];
  const seen = new Set();
  teddy.parts.forEach((part) => {
    if (seen.has(part.name)) return;
    seen.add(part.name);
    chips.push(part);
  });

  return (
    <section>
      <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
        <Heart className="h-6 w-6 text-primary" />
        The assembly lab
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Attaching is the third skill, and there are only three structural
        joints to learn (plus flat appliqué for details). Pull the bear apart
        and follow the dots: each color is a different way two parts get
        joined — matching dots meet when it assembles.{" "}
        <Link
          to="/sewing-primitives/make/teddy"
          className="font-medium text-primary hover:underline"
        >
          Full teddy page with cut list →
        </Link>
      </p>

      <div className="mt-5 grid gap-5 lg:grid-cols-[1.2fr,1fr]">
        <Card className="bg-card/60 backdrop-blur">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Teddy — parts ↔ bear</CardTitle>
            <CardDescription>{teddy.insight}</CardDescription>
          </CardHeader>
          <CardContent>
            <FoldViewer
              model={teddy}
              labels={{
                start: "apart",
                end: "assembled",
                button: "put it together",
                aria: "Assemble the bear from its parts",
                sceneAria: "3D view of a teddy bear assembling from primitive parts",
              }}
            />
          </CardContent>
        </Card>

        <div className="flex flex-col gap-5">
          <Card className="bg-card/60 backdrop-blur">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">The joints</CardTitle>
              <CardDescription>
                Every plush attachment ever is one of these.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {JOINTS.map((joint) => (
                <div key={joint.id} className="flex items-start gap-2.5 text-sm">
                  <span
                    aria-hidden
                    className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full border border-white/60"
                    style={{ backgroundColor: joint.color }}
                  />
                  <div>
                    <p className="font-semibold">{joint.name}</p>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      {joint.blurb}
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="rounded-xl border bg-card/50 p-4 backdrop-blur">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              parts list — nothing but lab primitives
            </p>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              {chips.map((part) => (
                <li key={part.id} className="flex items-start gap-1.5">
                  <Link
                    to={labHref(part.shape, part.params)}
                    className="shrink-0 rounded-full border border-primary/40 bg-primary/10 px-1.5 text-xs font-medium lowercase text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                  >
                    {part.name}
                  </Link>
                  <span className="text-xs leading-5">
                    {SHAPE_MAP[part.shape].name.toLowerCase()}
                    {part.note ? ` — ${part.note}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AssemblyLab;
